// =====================================
// src/pages/materials/order/Order_new.jsx
// =====================================

import { useParams, useLocation } from "react-router-dom";
import { useMemo, useState, useCallback } from "react";
import { v4 as uuid } from "uuid";

import FormContentManager from "../../../components/common/form/FormContentManager";
import TableCell from "../../../components/common/ui/TableCell";

export default function Order_new() {
  const { id: projectId, dns } = useParams();
  const location = useLocation();

  const psHeader = location.state?.psHeader;
  const psItems = location.state?.psItems;
  const fromDns = location.state?.from;

  // -----------------------------
  // HEADER
  // -----------------------------
  const [title, setTitle] = useState(
    psHeader?.title ? `[발주] ${psHeader.title}` : "발주서"
  );
  const [poDate, setPoDate] = useState(new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState("");
  const [reqDate, setReqDate] = useState("");
  const [deliveryPlace, setDeliveryPlace] = useState("");
  const [remark, setRemark] = useState("");

  // -----------------------------
  // ROW
  // -----------------------------
  const makeRow = () => ({
    _id: uuid(),
    code: "",
    name: "",
    spec: "",
    unit: "",
    qty: 0,
    price: 0,
    amount: 0,
    remark: "",
  });

  const initialRows = psItems
    ? psItems.map((r) => ({
        _id: uuid(),
        code: r.code,
        name: r.name,
        spec: r.spec,
        unit: r.unit,
        qty: r.qty || r.plan_qty || 0,
        price: 0,
        amount: 0,
        remark: "",
      }))
    : [];

  const [rowsData, setRowsData] = useState(
    initialRows.length > 0 ? initialRows : [makeRow()]
  );

  const updateCell = (id, key, value) =>
    setRowsData((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [key]: value } : r))
    );

  const rows = useMemo(
    () =>
      rowsData.map((r) => ({
        ...r,
        amount: Number(r.qty || 0) * Number(r.price || 0),
      })),
    [rowsData]
  );

  const total = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [rows]
  );

  // -----------------------------
  // SUMMARY
  // -----------------------------
  const summaryConfig = {
    title: "발주서 요약",
    sections: [
      { label: "제목", value: title },
      { label: "발주일", value: poDate },
      { label: "공급업체", value: supplier },
      { label: "납기 요청일", value: reqDate },
      { label: "납품 장소", value: deliveryPlace },
      { label: "비고", value: remark || "-" },
      { label: "합계금액", value: `${total.toLocaleString()} 원` },
    ],
  };

  // -----------------------------
  // BODY
  // -----------------------------
  const getBody = useCallback(() => {
    return {
      values: {
        ...(dns ? { dns } : {}),
        project: projectId,
        module: "material",
        doc_type: "PO",
        title,
        header: {
          po_date: poDate,
          supplier,
          request_date: reqDate,
          delivery_place: deliveryPlace,
          remark,
          from_ps_dns: fromDns,
        },
        lines: rows.map(({ _id, price, ...rest }) => ({
          ...rest,
          unit_price: Number(price) || 0,
          amount: (Number(rest.qty) || 0) * (Number(price) || 0),
        })),
        level2: "GEN",
      },
    };
  }, [
    projectId,
    dns,
    title,
    poDate,
    supplier,
    reqDate,
    deliveryPlace,
    remark,
    rows,
    fromDns,
  ]);

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <FormContentManager
      params={{ module: "material", project: projectId, dns }}
      docName="발주서"
      docDescribe="자재 발주 내용을 입력하세요."
      getBody={getBody}
      summaryConfig={summaryConfig}
      redirectTo={`/project/${projectId}/material/materialboard`}
    >
      {/* ================= HEADER TABLE ================= */}
      <table className="doc-table w-full mb-6">
        <tbody>
          <tr>
            <TableCell
              label="제목"
              editing
              colSpan={3}
              input={
                <input
                  className="input-base"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="발주일"
              editing
              input={
                <input
                  type="date"
                  className="input-base"
                  value={poDate}
                  onChange={(e) => setPoDate(e.target.value)}
                />
              }
            />
            <TableCell
              label="납기 요청일"
              editing
              input={
                <input
                  type="date"
                  className="input-base"
                  value={reqDate}
                  onChange={(e) => setReqDate(e.target.value)}
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="공급업체"
              editing
              input={
                <input
                  className="input-base"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                />
              }
            />
            <TableCell
              label="납품 장소"
              editing
              input={
                <input
                  className="input-base"
                  value={deliveryPlace}
                  onChange={(e) => setDeliveryPlace(e.target.value)}
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="비고"
              editing
              colSpan={3}
              input={
                <textarea
                  className="input-base min-h-[80px]"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
              }
            />
          </tr>
        </tbody>
      </table>

      {/* ================= ITEM TABLE ================= */}
      <div className="flex justify-between items-center mb-2">
        <div className="text-body-bold">발주 품목</div>
        <button
          className="btn-basic-sm"
          onClick={() => setRowsData((p) => [...p, makeRow()])}
        >
          + 행 추가
        </button>
      </div>

      <table className="doc-table w-full">
        <thead className="doc-thead">
          <tr>
            <th className="doc-th">코드</th>
            <th className="doc-th">품명</th>
            <th className="doc-th">규격</th>
            <th className="doc-th">단위</th>
            <th className="doc-th text-right">수량</th>
            <th className="doc-th text-right">단가</th>
            <th className="doc-th text-right">금액</th>
            <th className="doc-th">비고</th>
            <th className="doc-th-end"></th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r) => (
            <tr key={r._id}>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.code}
                  onChange={(e) => updateCell(r._id, "code", e.target.value)}
                />
              </td>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.name}
                  onChange={(e) => updateCell(r._id, "name", e.target.value)}
                />
              </td>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.spec}
                  onChange={(e) => updateCell(r._id, "spec", e.target.value)}
                />
              </td>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.unit}
                  onChange={(e) => updateCell(r._id, "unit", e.target.value)}
                />
              </td>
              <td className="doc-td text-right">
                <input
                  type="number"
                  className="input-sm text-right"
                  value={r.qty}
                  onChange={(e) => updateCell(r._id, "qty", e.target.value)}
                />
              </td>
              <td className="doc-td text-right">
                <input
                  type="number"
                  className="input-sm text-right"
                  value={r.price}
                  onChange={(e) => updateCell(r._id, "price", e.target.value)}
                />
              </td>
              <td className="doc-td text-right w-[120px]">
                {r.amount.toLocaleString()}
              </td>
              <td className="doc-td">
                <input
                  className="input-sm"
                  value={r.remark}
                  onChange={(e) => updateCell(r._id, "remark", e.target.value)}
                />
              </td>
              <td className="doc-td text-center">
                <button
                  className="doc-delete w-[30px]"
                  onClick={() =>
                    setRowsData((prev) =>
                      prev.length > 1
                        ? prev.filter((x) => x._id !== r._id)
                        : [makeRow()]
                    )
                  }
                >
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr>
            <th colSpan={6} className="doc-th text-right">
              합계
            </th>
            <th className="doc-th text-right">{total.toLocaleString()}</th>
            <th colSpan={2}></th>
          </tr>
        </tfoot>
      </table>
    </FormContentManager>
  );
}
